import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { Link } from 'dibs-elements/exports/Link.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { shortAddress, shortName } from 'dibs-crypto-utils/exports/formattingUtils.ts';
import { useAuctionCountdown } from 'dibs-crypto-utils/exports/useAuctionCountdown.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { NftItemTileAuctionCountdown } from './NftItemTileAuctionCountdown.tsx';
import { NftItemTilePrice } from './NftItemTilePrice.tsx';
import { NftItemTileAuctionDetailsFallback } from './NftItemTileAuctionDetailsFallback.tsx';

import styles from './styles/NftItemTileAuctionDetails.scss';

import { type NftItemTileAuctionDetails_nftAuction$key } from './__generated__/NftItemTileAuctionDetails_nftAuction.graphql.ts';
import { type NftItemTileAuctionDetails_nftItem$key } from './__generated__/NftItemTileAuctionDetails_nftItem.graphql.ts';

type Props = {
    nftAuction: NftItemTileAuctionDetails_nftAuction$key | null | undefined;
    nftItem: NftItemTileAuctionDetails_nftItem$key;
    showFiatPrice?: boolean;
    showOwner?: boolean;
    currency: string;
    persistedWalletAddress?: string | null;
};

const nftAuctionFragment = graphql`
    fragment NftItemTileAuctionDetails_nftAuction on NftAuction {
        status
        currentBid {
            ether
        }
        reservePrice {
            ether
        }
        winnerAddress {
            address
            publicProfile {
                profileName
            }
            ensDomain
        }
        endDate
        item {
            etherRates {
                ...NftItemTilePrice_etherRates
            }
        }
        endedConversionRates {
            rates {
                ...NftItemTilePrice_etherRates
            }
        }
    }
`;

const nftItemFragment = graphql`
    fragment NftItemTileAuctionDetails_nftItem on NftItem {
        ownerAddress {
            address
            publicProfile {
                profileName
            }
            ensDomain
        }
        ...NftItemTileAuctionDetailsFallback_nftItem
    }
`;

export const NftItemTileAuctionDetailsComponent: FC<Props> = ({
    nftAuction: nftAuctionRef,
    nftItem: nftItemRef,
    currency,
    showFiatPrice,
    showOwner = true,
    persistedWalletAddress,
}) => {
    const nftAuction = useFragment(nftAuctionFragment, nftAuctionRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const { ownerAddress } = nftItem;
    const { status, currentBid, reservePrice, endDate, winnerAddress, item, endedConversionRates } =
        nftAuction || {};
    const { countdownFinished } = useAuctionCountdown(endDate);

    let etherAmount = null;
    let etherRates = item?.etherRates;
    let priceLabel = (
        <FormattedMessage
            id="nftItemTile.auction.priceLabel.reserve"
            defaultMessage="Reserve Price"
        />
    );
    let bottomContent;

    if (status === 'PAUSED') {
        etherAmount = reservePrice;
    } else if (status === 'LIVE' && !currentBid) {
        etherAmount = reservePrice;
        bottomContent = (
            <FormattedMessage
                id="nftItemTile.auction.acceptingBids"
                defaultMessage="Accepting Bids"
            />
        );
    } else if (status === 'LIVE' && currentBid) {
        etherAmount = currentBid;
        priceLabel = (
            <>
                <FormattedMessage
                    id="nftItemTile.auction.priceLabel.currentBid"
                    defaultMessage="Current Bid"
                />
                {countdownFinished && persistedWalletAddress === winnerAddress?.address ? (
                    <span className={styles.badgeContainer}>
                        <Badge type="pill" size="small-heavy">
                            <FormattedMessage
                                id="nftItemTile.auction.priceLabel.winnerBadgeLabel"
                                defaultMessage="Winner"
                            />
                        </Badge>
                    </span>
                ) : null}
            </>
        );
        bottomContent = <NftItemTileAuctionCountdown endDate={endDate || ''} />;
    } else if (status === 'ENDED') {
        etherAmount = currentBid;
        etherRates = endedConversionRates?.rates;
        priceLabel = (
            <FormattedMessage
                id="nftItemTile.auction.priceLabel.sold"
                defaultMessage="Sold Price"
            />
        );
        bottomContent = showOwner ? (
            <FormattedMessage
                id="nftItemTile.auction.winner"
                defaultMessage="{owner} Owner"
                values={{
                    owner: (
                        <Link href={`/nft/address/ethereum/${ownerAddress?.address}`}>
                            {shortName(ownerAddress?.publicProfile?.profileName || '') ||
                                shortName(ownerAddress?.ensDomain || '') ||
                                shortAddress(ownerAddress?.address || '0')}
                        </Link>
                    ),
                }}
            />
        ) : null;
    }

    return (
        <>
            <div className={styles.withPadding}>
                <NftItemTilePrice
                    etherAmount={etherAmount?.ether ?? ''}
                    etherRates={etherRates?.filter(filterNulls) ?? null}
                    showFiatPrice={showFiatPrice}
                    currency={currency}
                />{' '}
                {priceLabel}
            </div>
            {bottomContent}
        </>
    );
};

export const NftItemTileAuctionDetails: FC<Props> = props => {
    const { nftAuction: nftAuctionRef, nftItem: nftItemRef } = props;

    const nftAuction = useFragment(nftAuctionFragment, nftAuctionRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);

    return nftAuction && nftAuction.status !== 'CANCELED' ? (
        <NftItemTileAuctionDetailsComponent {...props} />
    ) : (
        <NftItemTileAuctionDetailsFallback nftItem={nftItem} showOwner={props.showOwner} />
    );
};
